import { motion, MotionConfig } from 'framer-motion';

import { cn } from '@poseidon/components/cn';

import { SequenceSavedCheck } from '~/components/Sequence/SequenceSavedCheck';

/** Where the save a step's editor started has got to. */
export type StepSaveStatus = 'saving' | 'saved' | 'error';

const LABEL: Record<StepSaveStatus, string> = {
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Couldn’t save',
};

/** How much of the tab stays tucked under the step's tile (its half-width): the text clears it. */
const TUCKED_PX = 18;

interface Props {
  status: StepSaveStatus;
  /** The step tile's centre, in canvas content px — the tab's right end sits under it. */
  x: number;
  y: number;
}

/**
 * A step's save, shown AS PART of the step: a small tab that slides out from behind its tile (one layer under
 * it, so it reads as attached, and pans / zooms with it). While saving, a beam of light runs round the tab's
 * edge; once saved, a check draws itself in; if it couldn't, the tab turns red. Slides back under the tile when
 * it's done (see SequenceGraphEditor for how long "Saved" stays).
 */
export function SequenceStepSaveTab({ status, x, y }: Props) {
  return (
    // Respects "reduce motion": the tab appears and the check shows without moving.
    <MotionConfig reducedMotion="user">
      {/* Clips the tab while it's under the tile, so it emerges from behind it rather than sliding in over
          the canvas. Padded so its shadow isn't clipped too. */}
      <div
        className="pointer-events-none absolute z-[5] overflow-hidden py-2 pl-2"
        style={{ left: x, top: y, transform: 'translate(-100%, -50%)' }}>
        <motion.div
          data-step-save={status}
          role="status"
          layout
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 520, damping: 38 }}
          // The tab's ring: a 1.5px gap round the panel, filled by the status colour — and, while saving, by a
          // conic beam spinning behind the panel, so a bright arc runs round the edge.
          className={cn(
            'relative overflow-hidden rounded-l-full p-[1.5px] shadow-sm',
            status === 'saving' && 'bg-lynch-200 dark:bg-slate-700',
            status === 'saved' && 'bg-mantis-200 dark:bg-mantis-800',
            status === 'error' && 'bg-red-200 dark:bg-red-800',
          )}>
          {status === 'saving' && (
            <span
              aria-hidden
              className="absolute left-1/2 top-1/2 -ml-16 -mt-16 size-32 bg-[conic-gradient(from_0deg,transparent_0deg,transparent_240deg,#3b82f6_330deg,transparent_360deg)] motion-safe:animate-[spin_1.1s_linear_infinite]"
            />
          )}
          <span
            className="relative flex h-6 items-center gap-1.5 rounded-l-full bg-white pl-2 text-xs font-medium text-slate-600 dark:bg-slate-900 dark:text-slate-300"
            style={{ paddingRight: TUCKED_PX + 6 }}>
            {status === 'saved' && <SequenceSavedCheck />}
            {status === 'error' && <span className="size-1.5 rounded-full bg-red-500" />}
            <span className={cn('whitespace-nowrap', status === 'error' && 'text-red-600 dark:text-red-400')}>
              {LABEL[status]}
            </span>
          </span>
        </motion.div>
      </div>
    </MotionConfig>
  );
}
